import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * Hook genérico que encapsula toda a lógica CRUD
 * @param {Object} service - Serviço que estende BaseService
 * @param {Object} options - Opções de configuração
 * @param {number} options.pageSize - Tamanho padrão da página
 * @param {string} options.defaultSort - Ordenação padrão (ex: "field,asc")
 * @param {Object} options.defaultFilters - Filtros padrão
 * @returns {Object} Objeto com todos os estados e handlers do CRUD
 */
export function useCrud(service, options = {}) {
    const {
        pageSize = 5,
        defaultSort = 'id,asc',
        defaultFilters = {},
    } = options;

    // Estados de view
    const [view, setView] = useState('list'); // 'list', 'create', 'edit'
    const [editingItem, setEditingItem] = useState(null);

    // Estados de dados
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Estados de paginação
    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });

    // Estados de ordenação
    const [sorting, setSorting] = useState(() => {
        const [field, direction] = defaultSort.split(',');
        return { field, direction };
    });

    // Estados de filtros
    const [filters, setFilters] = useState(defaultFilters);
    const [localFilters, setLocalFilters] = useState(defaultFilters);

    // Refetch trigger
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const abortControllerRef = useRef(null);
    const serviceRef = useRef(service);
    serviceRef.current = service;

    const debouncedLocalFilters = useDebouncedValue(localFilters, 750);
    useEffect(() => {
        setFilters(debouncedLocalFilters);
        setPagination(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [debouncedLocalFilters]);

    // Fetch data quando dependências mudam
    useEffect(() => {
        // Cancela requisição anterior se existir
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const fetchData = async () => {
            const svc = serviceRef.current;
            if (!svc || !svc.list) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const result = await svc.list({
                    page: pagination.page,
                    size: pageSize,
                    sort: `${sorting.field},${sorting.direction}`,
                    ...filters,
                });

                // Só atualiza se a requisição não foi cancelada
                if (!abortController.signal.aborted) {
                    setData(result.content || []);

                    // Atualiza paginação (totalPages 0 quando sem registros)
                    const pageMeta = result.page || {};
                    const contentLength = (result.content || []).length;
                    const totalPages = contentLength === 0 ? 0 : (pageMeta.totalPages ?? 0) || 1;
                    const pageNumber = totalPages === 0 ? 0 : Math.min(pageMeta.number ?? 0, Math.max(0, totalPages - 1));
                    setPagination(prev => ({
                        ...prev,
                        page: pageNumber,
                        totalPages,
                        first: totalPages === 0 || pageNumber === 0,
                        last: totalPages === 0 || pageNumber >= totalPages - 1,
                    }));
                }
            } catch (error) {
                // Error fetching data
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        // Cleanup
        return () => {
            abortController.abort();
        };
    }, [
        pagination.page,
        sorting.field,
        sorting.direction,
        filters,
        refetchTrigger,
        pageSize,
    ]);

    // Handlers de paginação
    const handlePageChange = useCallback((newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    }, []);

    // Handlers de ordenação
    const handleSort = useCallback((field, direction) => {
        setSorting({ field, direction });
    }, []);

    // Handlers de filtros
    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const applyFilters = useCallback((nextFilters) => {
        const next = nextFilters != null ? nextFilters : localFilters;
        setLocalFilters(next);
        setFilters(next);
        setPagination(prev => ({ ...prev, page: 0 }));
        setRefetchTrigger(prev => prev + 1);
    }, [localFilters]);

    const handleSearch = useCallback(() => {
        applyFilters(localFilters);
    }, [applyFilters, localFilters]);

    // Handlers de CRUD
    const handleCreate = useCallback(async (formData) => {
        try {
            await service.create(formData);
            setView('list');
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            throw error;
        }
    }, [service]);

    const handleUpdate = useCallback(async (formData) => {
        try {
            if (editingItem?.id) {
                await service.update(editingItem.id, formData);
            }
            setView('list');
            setEditingItem(null);
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            throw error;
        }
    }, [service, editingItem]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir este item?')) {
            return;
        }

        try {
            await service.delete(id);
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: 'Erro ao excluir item',
            });
        }
    }, [service]);

    // Handlers de view
    const handleNew = useCallback(() => {
        setEditingItem(null);
        setView('create');
    }, []);

    const handleEdit = useCallback((item) => {
        setEditingItem(item);
        setView('edit');
    }, []);

    const handleCancel = useCallback(() => {
        setView('list');
        setEditingItem(null);
    }, []);

    const closeMessageModal = useCallback(() => {
        setMessageModal((m) => ({ ...m, open: false }));
    }, []);

    return {
        // View states
        view,
        setView,
        editingItem,
        setEditingItem,

        // Data states
        data,
        loading,

        // Pagination
        pagination,
        handlePageChange,

        // Sorting
        sorting,
        handleSort,

        // Filters
        filters,
        localFilters,
        setLocalFilters,
        handleFilterChange,
        handleSearch,
        applyFilters,

        // CRUD handlers
        handleCreate,
        handleUpdate,
        handleDelete,

        // View handlers
        handleNew,
        handleEdit,
        handleCancel,

        // Message modal (para erros de exclusão etc.)
        messageModal,
        closeMessageModal,

        // Refetch
        refetchTrigger,
        setRefetchTrigger,
    };
}
